import './ListboxGroup.css'
import { JSX } from 'react'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react'
import { CaretDownIcon } from '../icons'
import { Label } from '../InputLabel/InputLabel'
import { inputClasses } from '../Input/Input'
import { Tooltip } from '../Tooltip/Tooltip'
import Check from '../icons/CheckCircle/CheckCircle'
import cx from 'classnames'

type ListboxGroupOption = {
  value: string
  label: string
  disabled?: boolean
  tooltip?: string
}

const defaultOptionRender = ({
  selected,
  disabled,
  displayValue,
  isPlaceholder = false
}: {
  disabled?: boolean
  selected: boolean
  displayValue: string
  isPlaceholder?: boolean
}) => {
  return disabled ? (
    <div className="listbox-option-content-disabled">
      <span className="listbox-option-label-disabled">{displayValue}</span>
    </div>
  ) : (
    <div className="listbox-option-content">
      <span
        className={cx('listbox-option-label', isPlaceholder && 'listbox-option-label-placeholder')}
      >
        {displayValue}
      </span>
      {selected ? (
        <span className="text-green-400">
          <Check className="listbox-check-icon" aria-hidden="true" />
        </span>
      ) : null}
    </div>
  )
}

function ListboxGroupOption({
  option,
  isPlaceholder = false,
  renderOption = defaultOptionRender
}: {
  option: ListboxGroupOption
  isPlaceholder?: boolean
  renderOption?: (props: {
    selected: boolean
    disabled?: boolean
    displayValue: string
    isPlaceholder?: boolean
  }) => JSX.Element
}) {
  const optionContent = (
    <ListboxOption value={option.value} className="listbox-option" disabled={option.disabled}>
      {({ selected }) =>
        renderOption({
          selected,
          disabled: option.disabled,
          displayValue: option.label,
          isPlaceholder
        })
      }
    </ListboxOption>
  )

  return option.tooltip ? (
    <Tooltip content={option.tooltip} position="top">
      {optionContent}
    </Tooltip>
  ) : (
    optionContent
  )
}

export function ListboxGroup<T extends string>({
  required,
  options,
  value,
  onChange,
  disabled,
  label,
  hint,
  tooltip,
  tooltipPlacement = 'top',
  tooltipSize = 'small',
  placeholder = '-',
  hidePlaceholder = false,
  renderOption = defaultOptionRender
}: {
  required?: boolean
  options: ListboxGroupOption[]
  value: T | ''
  onChange: (value: T | '') => void
  disabled?: boolean
  label: string
  hint?: React.ReactNode
  tooltip?: React.ReactNode
  tooltipPlacement?: 'top' | 'bottom'
  tooltipSize?: 'small' | 'medium' | 'large'
  placeholder?: string
  hidePlaceholder?: boolean
  renderOption?: (props: {
    selected: boolean
    disabled?: boolean
    displayValue: string
    isPlaceholder?: boolean
  }) => JSX.Element
}) {
  return (
    <div className="listbox-group-container">
      {label && (
        <Label
          sizeType="sm"
          required={required}
          tooltip={tooltip}
          tooltipPlacement={tooltipPlacement}
          tooltipSize={tooltipSize}
        >
          {label}
        </Label>
      )}
      <Listbox value={value} onChange={onChange} disabled={disabled}>
        <div className="relative">
          <ListboxButton
            className={cx('listbox-button', inputClasses.sizeToClasses.md, inputClasses.fixed)}
          >
            {options.find(option => option.value === value)?.label || placeholder}
          </ListboxButton>
          <div className="listbox-icon-container">
            <CaretDownIcon className="listbox-icon" aria-hidden="true" />
          </div>
        </div>
        <ListboxOptions anchor="bottom" className="listbox-options">
          {!hidePlaceholder && (
            <ListboxGroupOption
              option={{
                value: '',
                label: placeholder,
                disabled: false
              }}
              isPlaceholder={true}
              renderOption={renderOption}
            />
          )}
          {options.map(option => (
            <ListboxGroupOption key={option.value} option={option} renderOption={renderOption} />
          ))}
        </ListboxOptions>
      </Listbox>
      {hint && <span className="listbox-hint">{hint}</span>}
    </div>
  )
}
